// implement a get users with workspaceaccess react query

import { useMutation, useQuery } from '@tanstack/react-query';
import {
  DiscoverableWorkspace,
  WorkspaceAccessResponse,
  getDiscoverableWorkspaces,
  getUserWorkspaces,
  getUsersWithAccessToSpace,
  getWorkspaceById,
  joinWorkspace,
  updateSpaceSettings,
} from '../api/workspace';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import Workspace from '../types/Workspace';
import { UpdateSpaceSettingsModel } from '../api/workspace';
import { queryClient } from '../queryClient';
import { QUERY_CONFIG } from '../constants/queryConfig';

export const useGetUsersWithWorkspaceAccess = (spaceId: number | null) => {
  return useQuery<WorkspaceAccessResponse[], Error>({
    queryKey: ['workspaceAccess', spaceId],
    queryFn: async () => (await getUsersWithAccessToSpace(spaceId)).data,
    enabled: !!spaceId,
    staleTime: QUERY_CONFIG.STALE_TIME,
  });
};

export const useGetWorkspaces = (organizationId: number | null) => {
  return useQuery<Workspace[], Error>({
    queryKey: ['workspaces', organizationId],
    queryFn: async () => (await getUserWorkspaces(organizationId)).data,
    enabled: !!organizationId,
    staleTime: QUERY_CONFIG.STALE_TIME,
  });
};

export const useGetDiscoverableWorkspaces = (organizationId: number | null) => {
  return useQuery<DiscoverableWorkspace[], Error>({
    queryKey: ['discoverableWorkspaces', organizationId],
    queryFn: async () => (await getDiscoverableWorkspaces(organizationId!)).data,
    enabled: !!organizationId,
    staleTime: QUERY_CONFIG.STALE_TIME,
  });
};

export const useJoinWorkspaceMutation = () => {
  return useMutation({
    mutationFn: async ({ spaceId }: { spaceId: number | string; organizationId: number }) => {
      return (await joinWorkspace(spaceId)).data;
    },
    onSuccess: async (_data, variables) => {
      // Refetch so the joined space moves into "Teamspaces" and drops out of discovery.
      await queryClient.refetchQueries({ queryKey: ['workspaces', variables.organizationId] });
      await queryClient.refetchQueries({ queryKey: ['discoverableWorkspaces', variables.organizationId] });
    },
  });
};

interface WorkspaceStore {
  currentWorkspace: string | null;
  setWorkspace: (workspaceId: string | null) => void;
}

export const useWorkspaceStore = create<WorkspaceStore>()(
  persist(
    (set) => ({
      currentWorkspace: null,
      setWorkspace: (workspaceId) => set({ currentWorkspace: workspaceId }),
    }),
    { name: 'workspace-store' }
  )
);

export const useGetActualSpaceId = () => {
  const { currentWorkspace } = useWorkspaceStore();

  return currentWorkspace;
};

export const useGetWorkspace = () => {
  const spaceId = useGetActualSpaceId();

  return useQuery<Workspace, Error>({
    queryKey: ['workspace', Number(spaceId)],
    queryFn: async () => {
      return (await getWorkspaceById(Number(spaceId))).data;
    },
    enabled: !!spaceId,
    staleTime: QUERY_CONFIG.STALE_TIME,
  });
};

// Hook to get workspace by ID (supports both numeric ID and publicId string)
export const useGetWorkspaceById = (workspaceId: string | number | null) => {
  return useQuery<Workspace, Error>({
    queryKey: ['workspace', workspaceId],
    queryFn: async () => {
      return (await getWorkspaceById(workspaceId!)).data;
    },
    enabled: !!workspaceId,
    staleTime: QUERY_CONFIG.STALE_TIME,
  });
};

export const useUpdateWorkspaceMutation = () => {
  return useMutation({
    mutationFn: async ({ spaceId, name, visibility }: { spaceId: number; name: string; visibility: string }) => {
      const updateModel: UpdateSpaceSettingsModel = {
        name: name,
        visibility: visibility,
      };
      const space = await updateSpaceSettings(spaceId, updateModel);

      // Update query
      queryClient.setQueryData(['workspaces', space.data.organizationId], (old: Workspace[]) => {
        const newData = old.map((savedSpace) => {
          if (space.data.id === savedSpace.id) {
            return space.data;
          }
          return savedSpace;
        });
        return newData;
      });

      return space.data;
    },
  });
};

export const useGetCurrentWorkspaceUsers = () => {
  const spaceId = useGetActualSpaceId();
  const workspaceUsersQuery = useGetUsersWithWorkspaceAccess(Number(spaceId));

  return workspaceUsersQuery;
};
